import React from 'react';
import Parse from 'parse';
import { Link } from 'react-router-dom';
import { lifeTaggerApi } from '../api/lifetaggerApi';
import { LifeCodeVisual } from '../beacons/qrCode.show';

import { usesResource } from './resourceUsage';

export default function ResourceWorkspace({ kind = 'qr' }) {
  const [resources, setResources] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [objects, setObjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [usageLoading, setUsageLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [usageError, setUsageError] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [attempt, setAttempt] = React.useState(0);
  const codeRef = React.useRef(null);
  const libraryCache = React.useRef(null);
  const codes = kind === 'qr';
  const audio = kind === 'audio';
  const title = codes ? 'LifeCodes' : audio ? 'Recognizable media' : 'Beacons';

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setSelected(null);
    setError('');
    libraryCache.current = null;
    const request = codes
      ? lifeTaggerApi.listLifeCodes({ limit: 1000 })
      : audio
      ? Parse.Cloud.run('getMedia')
      : lifeTaggerApi.listBluetoothBeacons({ limit: 1000 });
    request
      .then((items) => {
        if (!active) return;
        setResources(
          (items || []).map((item) => ({
            id: item.id || item.objectId || item.beaconId,
            anchorId: null,
            identifier: item.acrId || item.beaconId || item.identifier,
            type: kind,
            name:
              item.name ||
              item.title ||
              (codes ? 'LifeCode' : audio ? 'Media' : 'Beacon'),
            original: item,
          }))
        );
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [codes, audio, kind, attempt]);

  React.useEffect(() => {
    if (!selected) return undefined;
    let active = true;
    setObjects([]);
    setUsageError('');
    setUsageLoading(true);
    (async () => {
      if (libraryCache.current) {
        setObjects(
          libraryCache.current.filter((object) =>
            usesResource(object, selected)
          )
        );
        return;
      }
      const loaded = [];
      let page = 1;
      let more = true;
      while (active && more) {
        const result = await lifeTaggerApi.listLifeObjectsPage({
          page,
          limit: 100,
        });
        if (!active) return;
        loaded.push(...result.items);
        const matches = result.items.filter((object) =>
          usesResource(object, selected)
        );
        setObjects((current) => [
          ...current,
          ...matches.filter(
            (item) => !current.some((known) => known.id === item.id)
          ),
        ]);
        more = result.hasMore && result.items.length > 0;
        page += 1;
      }
      if (active) libraryCache.current = loaded;
    })()
      .catch((e) => active && setUsageError(e.message))
      .finally(() => active && setUsageLoading(false));
    return () => {
      active = false;
    };
  }, [selected, attempt]);

  const download = () => {
    const canvas = codeRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${selected.name}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <section className="lt-resources">
      <header className="lt-resource-heading">
        <div>
          <span className="lt-eyebrow">YOUR DISCOVERY TOOLS</span>
          <h1>{title}.</h1>
          <p>
            {codes
              ? 'Create a code. Print it. Choose what someone discovers.'
              : audio
              ? 'Sounds that can reveal your LifeObjects.'
              : 'Register a beacon you own and make the room a place to discover.'}
          </p>
        </div>
        <Link
          className="lt-primary"
          to={
            codes
              ? '/qrcode/create'
              : audio
              ? '/media'
              : '/signals/bluetooth/create'
          }
        >
          {codes
            ? 'Create LifeCode'
            : audio
            ? 'Upload recognition media'
            : 'Register beacon'}{' '}
          ↗
        </Link>
      </header>
      {selected ? (
        <>
          <button onClick={() => setSelected(null)}>← All {title}</button>
          <div className="lt-resource-detail">
            <div>
              <h2>{selected.name}</h2>
              {codes ? (
                <div ref={codeRef}>
                  <LifeCodeVisual lifeCode={selected.original} size={220} />
                </div>
              ) : (
                <p>
                  {audio ? (
                    selected.identifier ? (
                      'Fingerprint registered with ACRCloud'
                    ) : (
                      'Fingerprint missing'
                    )
                  ) : (
                    <>
                      Beacon ID: <strong>{selected.identifier}</strong>
                    </>
                  )}
                </p>
              )}
              {codes && (
                <p>
                  <button onClick={download}>Download code</button>{' '}
                  <Link
                    to={`/qrcode/details/${encodeURIComponent(
                      selected.identifier
                    )}`}
                  >
                    Print & manage code ↗
                  </Link>
                </p>
              )}
              <p>
                {codes
                  ? 'Scanning identifies this code. Each LifeObject still requires all of its audience, timing, and other discovery conditions.'
                  : audio
                  ? 'The phone must recognize this media through deliberate listening. Every other LifeObject condition must also match.'
                  : 'A nearby phone can detect this beacon. Every other condition on the LifeObject must also match.'}
              </p>
            </div>
            <div>
              <h2>
                LifeObjects using this{' '}
                {codes ? 'code' : audio ? 'media' : 'beacon'}
              </h2>
              <p>
                Open a LifeObject to view its content or edit its discovery
                requirements.
              </p>
              {objects.map((item) => (
                <Link
                  className="lt-resource-object"
                  key={item.id}
                  to={`/objects?preview=${encodeURIComponent(item.id)}`}
                >
                  <strong>{item.name}</strong>
                  <span>View LifeObject ↗</span>
                </Link>
              ))}
              {usageLoading && <p role="status">Checking your collection…</p>}
              {usageError && (
                <p role="alert">
                  Could not finish checking usage.{' '}
                  <button onClick={() => setAttempt((n) => n + 1)}>
                    Retry
                  </button>
                </p>
              )}
              {!usageLoading && !usageError && !objects.length && (
                <p>No LifeObjects use this resource yet.</p>
              )}
              <Link to="/objects">
                Choose a LifeObject from your collection ↗
              </Link>
            </div>
          </div>
        </>
      ) : (
        <>
          <input
            aria-label={`Search ${title}`}
            placeholder={`Search ${title.toLowerCase()}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {loading && <p role="status">Loading {title.toLowerCase()}…</p>}
          {error && (
            <p role="alert">
              {error}{' '}
              <button onClick={() => setAttempt((n) => n + 1)}>Retry</button>
            </p>
          )}
          <div className="lt-resource-grid">
            {resources
              .filter((item) =>
                `${item.name} ${item.identifier}`
                  .toLowerCase()
                  .includes(query.toLowerCase())
              )
              .map((item) => (
                <button
                  className="lt-resource-card"
                  key={item.id}
                  onClick={() => setSelected(item)}
                >
                  {codes ? (
                    <LifeCodeVisual lifeCode={item.original} size={120} />
                  ) : (
                    <span aria-hidden="true">◎</span>
                  )}
                  <h2>{item.name}</h2>
                  <p>
                    {codes
                      ? 'View code & LifeObjects'
                      : audio
                      ? item.identifier
                        ? 'Fingerprint registered'
                        : 'Fingerprint missing'
                      : `Beacon ID: ${item.identifier}`}
                  </p>
                  <span>Manage ↗</span>
                </button>
              ))}
          </div>
          {!loading && !error && !resources.length && (
            <p>No {title.toLowerCase()} yet. Start with the button above.</p>
          )}
          {resources.length >= 1000 && (
            <p>Showing the first 1,000 resources.</p>
          )}
        </>
      )}
    </section>
  );
}
